import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function HeroSection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedCat, setSelectedCat] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategorySelect = (cat: typeof categories[0]) => {
    setSelectedCat(cat.name);
    setDropdownOpen(false);
    window.open(cat.url, '_blank', 'noopener,nofollow');
  };

  return (
    <section className="relative w-full min-h-[520px] md:min-h-[620px] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Elegant%20modern%20ecommerce%20product%20showcase%20with%20diverse%20premium%20fashion%20accessories%20and%20lifestyle%20items%20arranged%20artistically%20on%20light%20cream%20marble%20surface%20soft%20directional%20studio%20lighting%20with%20subtle%20warm%20glow%20abstract%20geometric%20shadow%20patterns%20clean%20sophisticated%20editorial%20composition%20professional%20product%20photography%20luxurious%20minimalist%20aesthetic&width=1600&height=900&seq=hero-cssbuy-v3-2026&orientation=landscape"
          alt="CSSBuy Spreadsheet Products"
          className="w-full h-full object-cover object-top"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/40"></div>
      </div>

      <div className="relative z-10 w-full px-4 md:px-6 py-16 md:py-20 text-center">
        <div className="inline-flex items-center gap-2 bg-background-50/25 backdrop-blur-sm rounded-full px-4 py-1.5 mb-8">
          <span className="w-5 h-5 flex items-center justify-center bg-accent-500 rounded-full">
            <i className="ri-check-line text-[10px] text-primary-900"></i>
          </span>
          <span className="text-xs text-background-50 font-medium whitespace-nowrap">Atualizado em 2026 · +50k produtos verificados</span>
        </div>

        <h1 className="font-heading font-extrabold text-4xl md:text-6xl lg:text-7xl text-background-50 leading-[1.05] tracking-tight max-w-3xl mx-auto">
          CSSBuy Spreadsheet<br />
          <span className="text-accent-400">Portugal &amp; Brasil</span>
        </h1>

        <p className="mt-5 text-sm md:text-base text-background-50/80 max-w-xl mx-auto leading-relaxed">
          Encontre os melhores produtos, fornecedores confiáveis e links verificados no maior portal CSSBuy Spreadsheet em português.
        </p>

        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 bg-background-50 text-primary-900 px-7 py-3.5 rounded-full text-sm font-bold hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer w-full sm:w-auto justify-center"
          >
            CSSBuy Sheet
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-up-line text-sm"></i>
            </span>
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 border-2 border-background-50 text-background-50 px-7 py-3.5 rounded-full text-sm font-bold hover:bg-background-50 hover:text-primary-900 transition-colors whitespace-nowrap cursor-pointer w-full sm:w-auto justify-center"
          >
            Buy It Now
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-shopping-bag-line text-sm"></i>
            </span>
          </a>
        </div>

        <div className="mt-8 max-w-md mx-auto" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="w-full flex items-center justify-between bg-background-50 rounded-xl px-5 py-3.5 text-sm text-foreground-700 border border-background-200/70 hover:border-accent-500 transition-colors cursor-pointer"
          >
            <span className={selectedCat ? 'text-foreground-900 font-medium' : 'text-foreground-500'}>
              {selectedCat || 'Selecione uma categoria'}
            </span>
            <span className="w-5 h-5 flex items-center justify-center">
              <i className={`text-sm transition-transform duration-200 ${dropdownOpen ? 'ri-arrow-up-s-line rotate-180' : 'ri-arrow-down-s-line'}`}></i>
            </span>
          </button>
          {dropdownOpen && (
            <div className="absolute left-4 right-4 md:left-auto md:right-auto md:w-full max-w-md mt-2 bg-background-50 rounded-xl border border-background-200/70 shadow-lg overflow-hidden z-20 max-h-64 overflow-y-auto">
              {categories.map((cat) => (
                <button
                  key={cat.name}
                  onClick={() => handleCategorySelect(cat)}
                  className="w-full text-left px-5 py-3 text-sm text-foreground-700 hover:bg-accent-50 hover:text-foreground-900 transition-colors flex items-center justify-between cursor-pointer whitespace-nowrap"
                >
                  {cat.name}
                  <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                    <i className="ri-external-link-line text-xs"></i>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-2 max-w-2xl mx-auto">
          {categories.map((cat) => (
            <a
              key={cat.name}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-1.5 bg-background-50/15 backdrop-blur-sm hover:bg-accent-500/80 text-background-50 hover:text-primary-900 px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              {cat.name}
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-[10px]"></i>
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}